/**
 * AddSubconsultantCandidateModal — search an organization + pick its Consultant Service, creating a
 * Candidate-status Project Consultants row (services/projectConsultants.js). Commits immediately on
 * "Add" (a single atomic creation, unlike TeamMemberPickerModal's staged checkbox batch) — the
 * candidate then needs "Award" from its row's ⋯ menu before it becomes a real subconsultant branch
 * with people assignable.
 */

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { addCandidateConsultant, CONSULTANT_SERVICE_OPTIONS } from '../services/projectConsultants.js'
import { isAlderJamesOrg } from '../services/team.js'

const label = { fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500, color: 'var(--aj-text-mid)', marginBottom: 5, display: 'block' }
const input = { width: '100%', boxSizing: 'border-box', fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '8px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }
const btnBase = { border: 'none', borderRadius: 4, padding: '9px 20px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer' }

export default function AddSubconsultantCandidateModal({ projectId, organizations, existingOrgIds, clientOrgId, onClose, onAdded }) {
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [orgQuery, setOrgQuery] = useState('')
  const [service, setService] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  if (!projectId) return null

  const excludeIds = new Set(existingOrgIds || [])
  const candidateOrgs = (organizations || [])
    .filter(o => !isAlderJamesOrg(o) && o.id !== clientOrgId && !excludeIds.has(o.id))
    .filter(o => !orgQuery.trim() || (o.fields['Organization Name'] || '').toLowerCase().includes(orgQuery.trim().toLowerCase()))
    .sort((a, b) => (a.fields['Organization Name'] || '').localeCompare(b.fields['Organization Name'] || ''))

  async function submit() {
    if (!selectedOrg || !service) { setError('Choose an organization and a service.'); return }
    setSaving(true); setError('')
    try {
      await addCandidateConsultant(projectId, selectedOrg.id, service)
      onAdded()
    } catch (e) {
      setError((e && e.message) || 'Could not add the candidate.')
      setSaving(false)
    }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-modal-id="AddSubconsultantCandidateModal" style={{ background: 'var(--aj-bg-elevated)', borderRadius: 12, border: '0.5px solid var(--aj-border)', width: 420, maxWidth: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)' }}>
          <div style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 13, color: 'var(--aj-text)' }}>Add subconsultant candidate</div>
          <button onClick={onClose} disabled={saving} style={{ background: 'transparent', border: 'none', cursor: saving ? 'default' : 'pointer', color: 'var(--aj-text-muted)', padding: 4, opacity: saving ? 0.5 : 1 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div style={{ padding: '14px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label style={label}>Organization</label>
            {selectedOrg ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)' }}>
                <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' }}>{selectedOrg.fields['Organization Name'] || '(unnamed)'}</span>
                <button onClick={() => setSelectedOrg(null)} disabled={saving} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 12, fontFamily: '"DM Sans",sans-serif' }}>Change</button>
              </div>
            ) : (
              <>
                <input style={input} autoFocus value={orgQuery} placeholder="Search organizations…" onChange={e => setOrgQuery(e.target.value)} />
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 220, overflowY: 'auto' }}>
                  {candidateOrgs.length === 0 && (
                    <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', padding: '6px 2px' }}>No matching organizations.</div>
                  )}
                  {candidateOrgs.map(o => (
                    <button key={o.id} onClick={() => setSelectedOrg(o)}
                      style={{ textAlign: 'left', padding: '8px 10px', borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' }}>
                      {o.fields['Organization Name'] || '(unnamed)'}
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--aj-text-muted)' }}>{(o.fields['Type'] || []).join(', ')}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div>
            <label style={label}>Consultant service</label>
            <select style={input} value={service} disabled={saving} onChange={e => setService(e.target.value)}>
              <option value="">Choose a service…</option>
              {CONSULTANT_SERVICE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#C0392B' }}>{error}</div>}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '14px 20px', borderTop: '0.5px solid var(--aj-border)' }}>
          <button onClick={onClose} disabled={saving} style={{ ...btnBase, background: 'transparent', color: 'var(--aj-text-mid)', border: '0.5px solid var(--aj-border-mid)' }}>Cancel</button>
          <button onClick={submit} disabled={saving || !selectedOrg || !service} style={{ ...btnBase, background: '#07272D', color: '#fff', opacity: (saving || !selectedOrg || !service) ? 0.6 : 1 }}>
            {saving ? 'Adding…' : 'Add candidate'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
